// frontend/src/Components/SyncTo/RecordModal.jsx
// Combined mode-picker + recording UI.
//
// Single modal with two stacked sections:
//   • Top — mode (Note / Template) + template select + consent checkbox.
//           Editable while state === 'idle'; becomes a locked summary once
//           recording has started.
//   • Bottom — timer / level meter / Start | Pause | Resume | Cancel | Stop.
//           Start is disabled until a valid mode is picked AND consent is
//           ticked. Once recording starts, the bottom row swaps to the live
//           transport controls.
//
// Replaces the earlier two-modal flow (ModeModal then RecordModal). Consent
// is PATCHed to /api/sync-to/consent at the moment Start is clicked, before
// MediaRecorder.start() — same authoritative timestamp behaviour as before.
//
// All state + behaviour comes from useRecording() (Plan 30-04). Local state
// here is limited to the unconfirmed mode/template/consent values up until
// Start; once Start fires, those land on context.meta via updateMeta().
//
// Modal behaviour:
//   - backdrop='static' while recording (Pattern 8 — prevent accidental dismiss)
//   - keyboard=false while recording
//   - closeButton hidden while recording (only path off is Cancel/Stop)

import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Alert, Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useRecording } from './RecordingProvider';
import { notesToPages } from '../Templates/TemplateBuilder';
import './RecordModal.css';

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'http://localhost:3002';

const CONSENT_LABEL = 'Patient has consented to being recorded';
const FOREGROUND_TIP =
  'Tip — keep this tab foregrounded while recording. Some mobile browsers pause audio capture in background tabs.';

function getAuthHeader() {
  const token = typeof window !== 'undefined' ? sessionStorage.getItem('user') : null;
  return token ? { Authorization: 'Bearer ' + token } : {};
}

function readNameFromJwt() {
  try {
    const token = sessionStorage.getItem('user');
    if (!token) return '';
    const decoded = jwtDecode(token);
    const first = decoded?.firstName || '';
    const last = decoded?.lastName || '';
    const combined = (first + ' ' + last).trim();
    return combined || decoded?.email || '';
  } catch {
    return '';
  }
}

function readUserIdFromJwt() {
  try {
    const token = sessionStorage.getItem('user');
    if (!token) return null;
    const decoded = jwtDecode(token);
    return decoded?._id || decoded?.id || null;
  } catch {
    return null;
  }
}

function formatMMSS(ms) {
  if (!Number.isFinite(ms) || ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function RecordModal() {
  const rec = useRecording();
  const userName = useMemo(() => readNameFromJwt(), []);

  // Local pre-Start state. Hooks must run unconditionally — declare them
  // BEFORE the early-return for missing context (otherwise a transient null
  // context on first render would break the rules of hooks).
  const [mode, setMode] = useState(null);              // 'note' | 'template' | null
  const [templateId, setTemplateId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);
  const [consented, setConsented] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  // Inline cancel-confirmation banner (AddUser.jsx pattern). Opened by the
  // transport Cancel button; Yes runs cancel(), No just closes the banner.
  const [confirmCancel, setConfirmCancel] = useState(false);

  const modalOpen = !!rec?.modalOpen;
  const state = rec?.state || 'idle';

  // Reset local pre-Start state every time the modal (re)opens at idle. Don't
  // clobber while recording — those fields are locked at the top anyway.
  useEffect(() => {
    if (modalOpen && state === 'idle') {
      setMode(null);
      setTemplateId('');
      setTemplatesError(null);
      setConsented(false);
      setStarting(false);
      setStartError(null);
      setConfirmCancel(false);
    }
  }, [modalOpen, state]);

  // Lazy-load active templates the first time the user picks Template mode.
  useEffect(() => {
    if (!modalOpen || state !== 'idle') return;
    if (mode !== 'template') return;
    if (templates.length > 0) return;
    const userId = readUserIdFromJwt();
    if (!userId) {
      setTemplatesError('Could not identify user');
      return;
    }
    let cancelled = false;
    setLoadingTemplates(true);
    setTemplatesError(null);
    axios
      .get(`${URL}/templates/${userId}`, { headers: { ...getAuthHeader() } })
      .then((resp) => {
        if (cancelled) return;
        const all = Array.isArray(resp.data) ? resp.data : [];
        setTemplates(all.filter((t) => t && t.templateStatus === 'Active'));
      })
      .catch((err) => {
        if (cancelled) return;
        setTemplatesError(err?.response?.data?.message || err?.message || 'Failed to load templates');
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplates(false);
      });
    return () => { cancelled = true; };
  }, [modalOpen, state, mode, templates.length]);

  if (!rec) return null; // provider not mounted (dev safety)

  const {
    timer, level, meta,
    start, pause, resume, stop, cancel, closeModal, updateMeta,
    micDeniedOpen,       dismissMicDenied,
    firstSilenceOpen,    dismissFirstSilence,
    rollingSilenceOpen,  dismissRollingSilence,
    zeroByteFailureOpen, dismissZeroByteFailure,
    orphans, dismissOrphanPrompt, downloadOrphan, discardOrphan, resumeOrphan,
  } = rec;

  const recording = state === 'recording' || state === 'paused' || state === 'starting' || state === 'stopping';
  const paused = state === 'paused';
  // Lock the mode/consent block once we leave idle — even during 'starting'
  // we don't want the user toggling fields while MediaRecorder spins up.
  const topLocked = state !== 'idle';

  const startDisabled =
    starting ||
    state !== 'idle' ||
    !mode ||
    (mode === 'template' && !templateId) ||
    !consented;

  const handleStart = async () => {
    if (startDisabled) return;
    setStartError(null);
    setStarting(true);

    // 1. PATCH consent. Authoritative timestamp lives server-side; first-write
    //    wins on repeats so retrying after a transient failure is safe.
    if (meta?.sessionId) {
      try {
        await axios.patch(
          `${URL}/api/sync-to/consent`,
          { sessionId: meta.sessionId },
          { headers: { 'Content-Type': 'application/json', ...getAuthHeader() } },
        );
      } catch (err) {
        setStarting(false);
        setStartError(err?.response?.data?.error || err?.message || 'Consent write failed');
        return;
      }
    }

    // 2. Resolve template payload (pages snapshot used by ReviewModal Template
    //    variant). Existing capturedValues come straight from openModal's meta.
    //    Legacy templates only have `notes` (pre-pages schema) — fall back to
    //    notesToPages(notes) so ReviewModal receives a usable pages[] array.
    //    Without this fallback ReviewModal renders nothing for legacy templates.
    const chosen =
      mode === 'template'
        ? templates.find((t) => String(t._id) === String(templateId))
        : null;

    const resolvedPages =
      mode === 'template'
        ? (Array.isArray(chosen?.pages) && chosen.pages.length > 0
            ? chosen.pages
            : (Array.isArray(chosen?.notes) ? notesToPages(chosen.notes) : []))
        : undefined;

    const fullMeta = {
      ...(meta || {}),
      mode,
      templateId: mode === 'template' ? templateId : undefined,
      templatePages: resolvedPages,
    };

    // 3. Push the mode/template into context so onStopped can read it later
    //    when the worker completes.
    updateMeta?.(fullMeta);

    // 4. Kick off MediaRecorder. start() resolves once the stream is live; if
    //    the user denies the mic permission the provider's onMicDenied handler
    //    surfaces the warning sub-dialog and we fall back to idle.
    try {
      await start(fullMeta);
    } catch {
      // mic denied / other start failure handled provider-side via callbacks
    } finally {
      setStarting(false);
    }
  };

  const handlePause  = () => pause();
  const handleResume = () => resume();
  const handleStop   = async () => { await stop(); };
  // Cancel is now a two-step flow: clicking transport Cancel opens the
  // inline confirm banner; only the banner's Yes runs cancel().
  const handleCancel       = () => setConfirmCancel(true);
  const confirmCancelYes   = async () => { setConfirmCancel(false); await cancel(); };
  const confirmCancelNo    = () => setConfirmCancel(false);

  // Display-only template title once locked.
  const lockedTemplateTitle =
    meta?.templateId
      ? (templates.find((t) => String(t._id) === String(meta.templateId))?.templateTitle || '(template)')
      : '';

  return (
    <>
      {/* ── Combined Modal: mode picker (top) + recording UI (bottom) ── */}
      <Modal
        show={modalOpen}
        onHide={closeModal}
        size="lg"
        centered
        dialogClassName="responsive-modal"
        backdrop={recording ? 'static' : true}
        keyboard={!recording}
      >
        <Modal.Header
          closeButton={!recording}
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <div className="w-100">
            <Modal.Title as="h6" className="m-0">Record Session</Modal.Title>
            {meta?.patientLabel && (
              <span className="d-block text-muted small">{meta.patientLabel}</span>
            )}
          </div>
        </Modal.Header>

        <Modal.Body>
          {/* ── Top section — mode + template + consent ── */}
          {startError && state === 'idle' && (
            <Alert variant="danger" className="mb-3">{startError}</Alert>
          )}

          {/* Inline cancel-confirmation banner (AddUser.jsx pattern). */}
          {confirmCancel && (
            <Alert
              variant="warning"
              className="d-flex align-items-center justify-content-between fs-6 fw-normal"
            >
              <span>Are you sure you want to discard this recording? This cannot be undone.</span>
              <div className="ms-auto d-flex">
                <Button variant="outline-danger" className="me-1" onClick={confirmCancelYes}>
                  Yes, discard
                </Button>
                <Button variant="outline-secondary" onClick={confirmCancelNo}>
                  Keep recording
                </Button>
              </div>
            </Alert>
          )}

          {/* Inline first-silence banner (replaces former separate modal). */}
          {firstSilenceOpen && (
            <Alert
              variant="warning"
              className="d-flex align-items-center justify-content-between fs-6 fw-normal"
            >
              <span>
                We can&apos;t hear you — your mic may be muted or misconfigured. Check it and try again.
              </span>
              <div className="ms-auto d-flex">
                <Button variant="primary" className="me-1" onClick={dismissFirstSilence}>
                  Retry
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={async () => { dismissFirstSilence(); setConfirmCancel(true); }}
                >
                  Stop
                </Button>
              </div>
            </Alert>
          )}

          {/* Inline rolling-silence banner (replaces former separate modal). */}
          {rollingSilenceOpen && (
            <Alert
              variant="info"
              className="d-flex align-items-center justify-content-between fs-6 fw-normal"
            >
              <span>
                Still recording? We haven&apos;t heard anything for 30 seconds — auto-stop at 60s of silence.
              </span>
              <div className="ms-auto d-flex">
                <Button variant="primary" className="me-1" onClick={dismissRollingSilence}>
                  Keep recording
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={async () => { dismissRollingSilence(); await stop(); }}
                >
                  Stop now
                </Button>
              </div>
            </Alert>
          )}

          <div className="mb-3">
            <div className="small text-muted mb-2">Choose what to capture:</div>
            <div className="d-flex gap-2">
              <Button
                variant={(topLocked ? meta?.mode : mode) === 'note' ? 'primary' : 'outline-primary'}
                onClick={() => setMode('note')}
                disabled={topLocked || starting}
                aria-pressed={(topLocked ? meta?.mode : mode) === 'note'}
              >
                <i className="fa-light fa-notes-medical me-2" />
                Note
              </Button>
              <Button
                variant={(topLocked ? meta?.mode : mode) === 'template' ? 'primary' : 'outline-primary'}
                onClick={() => setMode('template')}
                disabled={topLocked || starting}
                aria-pressed={(topLocked ? meta?.mode : mode) === 'template'}
              >
                <i className="fa-light fa-file-lines me-2" />
                Template
              </Button>
            </div>
          </div>

          {((topLocked && meta?.mode === 'template') || (!topLocked && mode === 'template')) && (
            <Form.Group className="mb-3">
              <Form.Label className="small text-muted mb-1">Active template</Form.Label>
              {topLocked ? (
                <Form.Control type="text" value={lockedTemplateTitle} disabled readOnly />
              ) : loadingTemplates ? (
                <div className="d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" />
                  <span className="small text-muted">Loading active templates…</span>
                </div>
              ) : templatesError ? (
                <Alert variant="warning" className="mb-0 py-2 small">{templatesError}</Alert>
              ) : templates.length === 0 ? (
                <Alert variant="info" className="mb-0 py-2 small">
                  No active templates for this practice. Publish a template first or choose Note mode.
                </Alert>
              ) : (
                <Form.Select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  disabled={starting}
                  aria-label="Active template selector"
                >
                  <option value="">Select a template…</option>
                  {templates.map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.templateTitle || '(untitled)'}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>
          )}

          <Form.Group className="mb-2">
            <Form.Check
              type="checkbox"
              id="syncto-consent-checkbox"
              checked={topLocked ? true : consented}
              onChange={(e) => setConsented(e.target.checked)}
              disabled={topLocked || starting}
              label={
                <span>
                  <strong>{CONSENT_LABEL}</strong>
                  {userName && (
                    <span className="text-muted ms-2">(You: {userName})</span>
                  )}
                </span>
              }
            />
          </Form.Group>

          {!topLocked && (
            <div className="small text-muted mb-3">{FOREGROUND_TIP}</div>
          )}

          <hr className="my-3" />

          {/* ── Bottom section — timer + level + transport buttons ── */}
          <div className="d-flex align-items-center gap-3 mb-3">
            <span
              className={`syncto-pulse-dot${paused || state === 'idle' ? ' syncto-pulse-paused' : ''}`}
              style={state === 'idle' ? { opacity: 0.35 } : undefined}
            />
            <span className="syncto-timer">{formatMMSS(timer)}</span>
            <span className="text-muted ms-auto small">
              {state === 'idle'      && (consented && mode && (mode !== 'template' || templateId) ? 'Ready to record' : 'Pick a mode and tick consent')}
              {state === 'starting'  && 'Requesting microphone…'}
              {state === 'recording' && 'Recording'}
              {state === 'paused'    && 'Paused'}
              {state === 'stopping'  && 'Finishing…'}
            </span>
          </div>

          <div className="syncto-level-meter mb-3" style={state === 'idle' ? { opacity: 0.45 } : undefined}>
            <div
              className={`syncto-level-meter-bar${paused ? ' syncto-level-meter-paused' : ''}`}
              style={{ width: `${Math.min(100, Math.max(0, Math.round((level || 0) * 100)))}%` }}
              aria-label="Microphone level"
            />
          </div>

          <div className="d-flex justify-content-end gap-2">
            {state === 'idle' && (
              <Button variant="primary" onClick={handleStart} disabled={startDisabled}>
                {starting ? (
                  <><Spinner animation="border" size="sm" className="me-2" />Starting…</>
                ) : (
                  <><i className="fa-light fa-microphone-lines me-2" />Start</>
                )}
              </Button>
            )}
            {state === 'recording' && (
              <>
                <Button variant="outline-secondary" onClick={handlePause}>Pause</Button>
                <Button variant="outline-danger"  onClick={handleCancel}>Cancel</Button>
                <Button variant="danger"          onClick={handleStop}>Stop</Button>
              </>
            )}
            {state === 'paused' && (
              <>
                <Button variant="primary"         onClick={handleResume}>Resume</Button>
                <Button variant="outline-danger"  onClick={handleCancel}>Cancel</Button>
                <Button variant="danger"          onClick={handleStop}>Stop</Button>
              </>
            )}
            {(state === 'starting' || state === 'stopping') && (
              <Button variant="secondary" disabled>{state === 'starting' ? 'Starting…' : 'Stopping…'}</Button>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* ── SYNC-04 sub-dialog: mic permission denied ── */}
      <Modal show={micDeniedOpen} onHide={dismissMicDenied} centered>
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">Microphone access needed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-2">SyncTo needs microphone access to record this session.</Alert>
          <p className="mb-1">
            Click the lock or camera icon in your browser&apos;s address bar and allow microphone access for this site,
            then click Start again.
          </p>
          <p className="small text-muted mb-0">
            Chrome / Edge: Settings → Privacy and security → Site Settings → Microphone.{' '}
            Firefox: Preferences → Privacy &amp; Security → Permissions → Microphone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={dismissMicDenied}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* SYNC-04 first-silence and rolling-silence prompts now render inline
          inside the main RecordModal body (above the mode picker) — see the
          firstSilenceOpen / rollingSilenceOpen Alert blocks. */}

      {/* ── PITFALLS #5 sub-dialog: 0-byte chunks ── */}
      <Modal show={zeroByteFailureOpen} onHide={dismissZeroByteFailure} centered>
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">Recording failed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger" className="mb-2">Your browser produced empty audio chunks for 25 seconds.</Alert>
          <p>This usually means the microphone is disconnected or the browser tab lost audio access. Please close this dialog and try again.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={dismissZeroByteFailure}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ── D-08 orphan prompt ── */}
      <Modal show={(orphans?.length || 0) > 0} onHide={dismissOrphanPrompt} centered size="lg">
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">Previous recording found</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">We found {orphans.length} unfinished recording{orphans.length === 1 ? '' : 's'} from a previous session. Download and save, or discard.</p>
          <ul className="list-unstyled m-0">
            {orphans.map((o) => (
              <li key={o.sessionKey} className="border-bottom py-2 d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <div className="fw-semibold">{o.patientLabel || '(no patient label)'}</div>
                  <div className="text-muted small">
                    Started {new Date(o.startedAt).toLocaleString()} — ~{Math.max(1, Math.round(((o.lastSeq + 1) * 5) / 60))} min of audio
                  </div>
                </div>
                <Button size="sm" variant="primary"         onClick={() => resumeOrphan(o.sessionKey)}>Resume</Button>
                <Button size="sm" variant="outline-primary" onClick={() => downloadOrphan(o.sessionKey)}>Download</Button>
                <Button size="sm" variant="outline-danger"  onClick={() => discardOrphan(o.sessionKey)}>Discard</Button>
              </li>
            ))}
          </ul>
          <p className="text-muted small mt-3 mb-0">
            Note: &quot;Resume&quot; downloads the existing audio — continuing recording where you left off will arrive in a later release.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={dismissOrphanPrompt}>Dismiss all</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
